import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import { ObjectHash, encodeQueryString } from '../../../../app/queryString'
import {
  HoldingOptionType,
  HoldingTransactionType,
  InsiderView,
  InsiderViewHasParams,
  InsiderViewParam,
  InsiderViewsParams,
  InvestorType,
} from '../constants'

function getInsiderViewSpecificParams<
  T extends InsiderView,
  ParamsType extends Partial<InsiderViewsParams[T]> = Partial<InsiderViewsParams[T]>,
>(params: ParamsType, view: T) {
  return Object.keys(params).reduce(
    (acc, key) =>
      InsiderViewHasParams[view]?.includes(key as InsiderViewParam)
        ? { ...acc, [key]: params[key as keyof ParamsType] }
        : acc,
    {} as ParamsType
  )
}

export function useInsiderTradingRouter<T extends InsiderView>() {
  const [params] = useSearchParams()

  return React.useMemo(() => {
    const view = (params.get(InsiderViewParam.View) || InsiderView.Index) as T

    const parsedParams: InsiderViewsParams[T] = {
      [InsiderViewParam.View]: view,
      [InsiderViewParam.Page]: Number(params.get(InsiderViewParam.Page) || '1'),
      [InsiderViewParam.Search]: params.get(InsiderViewParam.Search) ?? undefined,
      [InsiderViewParam.InvestorId]: params.get(InsiderViewParam.InvestorId) ?? '',
      [InsiderViewParam.ReportEndDate]: params.get(InsiderViewParam.ReportEndDate) ?? undefined,
      [InsiderViewParam.TransactionType]:
        (params.get(InsiderViewParam.TransactionType) as HoldingTransactionType) ?? undefined,
      [InsiderViewParam.OptionType]: (params.get(InsiderViewParam.OptionType) as HoldingOptionType) ?? undefined,
      [InsiderViewParam.AssetCategory]: params.get(InsiderViewParam.AssetCategory) ?? undefined,
      [InsiderViewParam.Sort]: params.get(InsiderViewParam.Sort) ?? undefined,
      [InsiderViewParam.InvestorType]: params.get(InsiderViewParam.InvestorType) as InvestorType,
    }

    return {
      params,
      viewParams: getInsiderViewSpecificParams(parsedParams, view),
      getInsiderTradingLink: <View extends InsiderView>({
        view,
        params = {},
        additionalQueryParams,
      }: {
        view: View
        params?: Partial<InsiderViewsParams[View]>
        additionalQueryParams?: ObjectHash
      }) =>
        `/insidertrading.ashx?${encodeQueryString({
          ...getInsiderViewSpecificParams({ ...params, view }, view),
          ...additionalQueryParams,
        })}`,
    }
  }, [params])
}
